import isTizen from '@accedo/xdk-device-samsung-tizen/esm/detection'
import { Lightning, Registry, Router } from '@lightningjs/sdk'
import SpeechComp from '../../components/speechComp/speechComp'
import { getColor, grey200, grey300, grey700, white } from '../../constants/colors'
import { UniNeueHeavy, UniNeueRegular } from '../../constants/fonts'
import { BORDER_RADIUS } from '../../constants/general'
import { Images } from '../../constants/paths'
import { HOME } from '../../constants/routes'
import { AppConfig } from '../../services/appConfig'
import { storeUserData, updateUserSubscriptionData } from '../../utils/authentication'
import { getText } from '../../utils/dictionary'
import { getAsset } from '../../utils/image'
import { createRequest, generateQR, isError, type WrappedRequest } from '../../utils/misc'
import { captureSessionData } from '../../utils/segmentAnalytics'
import { stringWithSpace } from '../../utils/text'

const POLLING_INTERVAL = 5000

export default class LoginSmartphoneHandler extends SpeechComp implements ITab {
  private _intervalId: number
  private _pollingInterval: number
  private _code: string
  private _goBack: boolean
  private _getActivationCode: WrappedRequest<typeof AppConfig.shared.authService.getActivationCode>
  private _goSteps: number

  static _template() {
    return {
      MainContainer: {
        w: 1920,
        h: 888,
        rect: true,
        texture: Lightning.Tools.getSvgTexture(
          getAsset(Images.LOGIN_WITH_SMARTPHONE_BG),
          1920,
          888,
        ),
        Step1Container: {
          x: 316,
          h: 768,
          w: 600,
          flex: {
            direction: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: 64,
          },
          Title: {
            text: {
              text: getText('loginStep1Title'),
              textColor: getColor(white),
              fontSize: 48,
              lineHeight: 56,
              textAlign: 'left',
              fontFace: UniNeueHeavy,
              wordWrapWidth: 600,
              advancedRenderer: true,
              maxLines: 1,
              maxLinesSuffix: '...',
            },
          },
          Description: {
            text: {
              text: getText('loginStep1Description'),
              textColor: getColor(grey300),
              fontSize: 28,
              lineHeight: 36,
              textAlign: 'left',
              fontFace: UniNeueRegular,
              wordWrapWidth: 600,
              advancedRenderer: true,
              maxLines: 2,
              maxLinesSuffix: '...',
            },
          },
          QRCode: {
            flexItem: {
              marginTop: 16,
            },
            w: 500,
            h: 500,
            rect: true,
            color: getColor(white),
            shader: null,
            src: '',
          },
          Description2: {
            flexItem: {
              marginTop: 16,
            },
            w: 600,
            text: {
              text: getText('loginStep1Description2'),
              textColor: getColor(grey200),
              fontSize: 28,
              lineHeight: 40,
              textAlign: 'left',
              fontFace: UniNeueRegular,
              wordWrapWidth: 600,
              maxLines: 1,
              advancedRenderer: true,
            },
          },
        },
        Step2Container: {
          x: 1016,
          h: 280,
          w: 604,
          flex: {
            direction: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: 64,
          },
          Title: {
            text: {
              text: getText('loginStep2Title'),
              textColor: getColor(white),
              fontSize: 48,
              lineHeight: 56,
              textAlign: 'left',
              fontFace: UniNeueHeavy,
              wordWrapWidth: 600,
              advancedRenderer: true,
              maxLines: 1,
              maxLinesSuffix: '...',
            },
          },
          Description: {
            text: {
              text: getText('loginStep2Description'),
              textColor: getColor(grey300),
              fontSize: 28,
              lineHeight: 36,
              textAlign: 'left',
              fontFace: UniNeueRegular,
              wordWrapWidth: 600,
              advancedRenderer: true,
              maxLines: 2,
              maxLinesSuffix: '...',
            },
          },
          CodeContainer: {
            flexItem: {
              marginTop: 10,
            },
            h: 72,
            rect: true,
            color: getColor(grey700),
            shader: {
              type: Lightning.shaders.RoundedRectangle,
              radius: BORDER_RADIUS,
            },
            flex: {
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: 16,
              paddingBottom: 0,
              paddingLeft: 24,
              paddingRight: 24,
            },
            Code: {
              flexItem: { margin: 0, padding: 0 },
              text: {
                text: '',
                color: getColor(white),
                fontSize: 64,
                fontFace: UniNeueHeavy,
                lineHeight: 64,
                letterSpacing: 0.64,
              },
            },
          },
        },
      },
    }
  }

  get code() {
    return this.tag('Code')
  }

  get pageText() {
    return 'Login With Smartphone'
  }

  get sectionText() {
    return 'Scroll Right to Login with Remote Control'
  }

  get speechText() {
    return (
      getText('loginStep1Title') +
      ' . . ' +
      getText('loginStep1Description') +
      ' . . ' +
      getText('loginStep2Title') +
      ' . . ' +
      getText('loginStep2Description') +
      ' . . Activation Code: ' +
      this.code?.text?.text
    )
  }

  get qrCode() {
    return this.tag('QRCode')
  }

  set goBack(v: boolean) {
    this._goBack = v
  }

  set goSteps(v: number) {
    this._goSteps = v
  }

  _inactive() {
    this.stopPolling()
    this._getActivationCode.cancel()
  }

  async _active() {
    this._getActivationCode = createRequest(
      AppConfig.shared.authService.getActivationCode.bind(AppConfig.shared.authService),
      { preventConcurrent: true },
    )

    this.fireAncestors('$toggleLoading', true)
    await this.setQR(getText('loginStep1QrCodeUrl'))
    this.fireAncestors('$toggleLoading', false)

    await this.setPolling()
    this.speak()
  }

  async setPolling() {
    const response = await this._getActivationCode.fetch()

    if (!response) {
      return
    }

    if (isError(response)) {
      if (response.isNetworkError) {
        this.fireAncestors('$connectionError', {
          title: getText('nonBffConnectionErrorTitle'),
          info: getText('connectionErrorSubtitle'),
          footer: getText('loginRemoteErrorFooter'),
          retryAction: () => {
            Router.reload()
            Router.focusPage()
          },
        })
      } else {
        this.fireAncestors('$smartPhoneError', {
          title: getText('activationCodeError'),
          info: getText('tryPasswordSignIn'),
        })
      }

      return
    }

    this._code = response.activation_code

    this._pollingInterval = POLLING_INTERVAL

    if (this.code && this._code && this._pollingInterval && this.active) {
      this.code.patch({ text: stringWithSpace(this._code) })
      this.startPolling()
    }
  }

  startPolling() {
    this._intervalId = Registry.setInterval(async () => {
      await this.pollCodeStatus()
    }, this._pollingInterval)
  }

  stopPolling() {
    Registry.clearInterval(this._intervalId)
    this._intervalId = null
  }

  async pollCodeStatus() {
    if (!this._code) {
      return
    }

    const { authService } = AppConfig.shared
    const data = await authService.getActivationCodeStatus({ code: this._code })
    const authToken = data?.token
    const refreshToken = data?.refresh_token

    if (!authToken || !refreshToken) {
      return
    }

    storeUserData(data)
    // Update user subscription data if needed
    updateUserSubscriptionData()

    AppConfig.shared.appSettings.homeData = null

    if (isTizen) {
      await AppConfig.shared.samsungCWService.restoreContinueWatching()
    }

    this.stopPolling()
    this._code = ''

    void captureSessionData()

    if (this._goBack) {
      Router.go(this._goSteps)
    } else {
      Router.navigate(HOME)
    }
  }

  async setQR(textToQr: string) {
    this.qrCode?.patch({ src: await generateQR(textToQr) })
  }

  getFirstFocusElement(): Lightning.Component {
    return null
  }

  getFocusedElement(): Lightning.Component {
    return null
  }

  focusFirstElement(): void {}

  isAtTopMostElement(): boolean {
    return false
  }
}
